/**
 * @fileoverview Onboarding Checklist Component
 * 
 * Progressive checklist for first-time users to guide them through
 * the EvalOps setup process. Replaces KPI cards during onboarding.
 * 
 * @author Claude Code
 * @since 2024-01-01
 */

import React, { useState, useEffect } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { 
  Key, 
  Play, 
  Rocket,
  ExternalLink,
  CheckCircle
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { trackEvent } from '@/lib/analytics';
import { useAuth } from '@/lib/auth/useAuth';
import { getApiKeys } from '@/api/api-keys';
import OnboardingKpiCards from './OnboardingKpiCards';
import OnboardingWizardStep from './OnboardingWizardStep';

interface ChecklistStep {
  id: string;
  title: string;
  description: string;
  icon: React.ReactNode;
  completed: boolean;
  action?: {
    label: string;
    onClick: () => void;
  };
}

/**
 * Props for the OnboardingChecklist component
 */
interface OnboardingChecklistProps {
  /** Callback when user completes onboarding */
  onComplete?: () => void;
}

/**
 * Onboarding Checklist Component
 * 
 * Guides new users through the essential setup steps:
 * 1. Generate API Key
 * 2. Install CLI/Package
 * 3. Run Sample Test
 * 4. Explore Features
 * 
 * @param props - Component props
 * @returns Rendered onboarding checklist
 */
const OnboardingChecklist: React.FC<OnboardingChecklistProps> = ({ onComplete }) => {
  const [steps, setSteps] = useState<ChecklistStep[]>([]);
  const [completedCount, setCompletedCount] = useState(0);
  const navigate = useNavigate();
  const { user } = useAuth();

  /**
   * Initialize and check step completion status
   */
  useEffect(() => {
    const initializeSteps = async () => {
      // Check if user has API keys
      let hasApiKeys = false;
      try {
        if (user?.id) {
          const response = await getApiKeys(user.id);
          hasApiKeys = response.success && response.data && response.data.length > 0;
        }
      } catch (error) {
        console.error('Error checking API keys:', error);
      }

      const initialSteps: ChecklistStep[] = [
        {
          id: 'api-key',
          title: 'Create API Key',
          description: 'Generate credentials to connect your tests',
          icon: <Key className="h-5 w-5" />,
          completed: hasApiKeys,
          action: !hasApiKeys ? {
            label: 'Create Key',
            onClick: () => {
              // Try to open the modal directly
              if ((window as Window & { openApiKeyModal?: () => void }).openApiKeyModal) {
                (window as Window & { openApiKeyModal?: () => void }).openApiKeyModal();
              } else {
                // Fallback: Scroll to API key CTA
                const element = document.querySelector('[data-component="api-key-cta"]');
                if (element) {
                  element.scrollIntoView({ behavior: 'smooth' });
                }
              }
              trackEvent('onboarding_step_clicked', { step: 'api-key' });
            }
          } : undefined
        },
        {
          id: 'run-test',
          title: 'Run First Test',
          description: 'Execute a sample test to verify your setup',
          icon: <Play className="h-5 w-5" />,
          completed: false, // Would check if user has any test runs
          action: {
            label: 'Run Test',
            onClick: () => {
              // Scroll to sample test CTA
              const element = document.querySelector('[data-component="sample-test-cta"]');
              if (element) {
                element.scrollIntoView({ behavior: 'smooth' });
              }
              trackEvent('onboarding_step_clicked', { step: 'run-test' });
            }
          }
        },
        {
          id: 'explore',
          title: 'Explore Dashboard',
          description: 'View your test results and set up monitoring',
          icon: <Rocket className="h-5 w-5" />,
          completed: false,
          action: {
            label: 'View Results',
            onClick: () => {
              navigate('/runs');
              trackEvent('onboarding_step_clicked', { step: 'explore' });
            }
          }
        }
      ];

      setSteps(initialSteps);
      setCompletedCount(initialSteps.filter(step => step.completed).length);
    };

    initializeSteps();
  }, [user?.id, navigate]);

  /**
   * Handle marking a step as complete
   */
  const handleStepComplete = (stepId: string) => {
    setSteps(prevSteps => {
      const updatedSteps = prevSteps.map(step => 
        step.id === stepId ? { ...step, completed: true } : step
      );
      
      const newCompletedCount = updatedSteps.filter(step => step.completed).length;
      setCompletedCount(newCompletedCount);
      
      // Track completion
      trackEvent('onboarding_step_completed', { 
        step: stepId,
        total_completed: newCompletedCount,
        total_steps: updatedSteps.length
      });
      
      // Check if all steps are complete
      if (newCompletedCount === updatedSteps.length) {
        trackEvent('onboarding_completed', {
          total_steps: updatedSteps.length
        });
        onComplete?.();
      }
      
      return updatedSteps;
    });
  };

  const progressPercentage = Math.round((completedCount / steps.length) * 100);

  return (
    <Card className="mb-6">
      <CardContent className="p-6">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className="text-xl font-semibold">Welcome to EvalOps!</h2>
            <p className="text-lg font-medium text-primary mb-2">
              EvalOps keeps AI features from silently breaking in prod — run a test to see if today's build is safe.
            </p>
            <p className="text-muted-foreground text-sm">
              Complete these steps to get started with test automation
            </p>
          </div>
          <div className="text-right">
            <div className="text-2xl font-bold text-primary">
              {completedCount}/{steps.length}
            </div>
            <div className="text-xs text-muted-foreground">completed</div>
          </div>
        </div>

        {/* Progress Bar */}
        <div className="w-full bg-gray-200 rounded-full h-2 mb-6">
          <div 
            className="bg-primary h-2 rounded-full transition-all duration-300 ease-out"
            style={{ width: `${progressPercentage}%` }}
          />
        </div>

        {/* Demo KPI Cards */}
        <OnboardingKpiCards />

        {/* Linear Wizard Steps */}
        <div className="space-y-4">
          {steps.map((step, index) => {
            const isCurrentStep = !step.completed && steps.slice(0, index).every(s => s.completed);
            const isPastStep = step.completed;
            const isFutureStep = !step.completed && !isCurrentStep;
            
            return (
              <OnboardingWizardStep
                key={step.id}
                step={step}
                index={index}
                isCurrentStep={isCurrentStep}
                isPastStep={isPastStep}
                isFutureStep={isFutureStep}
                onStepComplete={handleStepComplete}
              />
            );
          })}
        </div>

        {/* Completion Message */}
        {completedCount === steps.length && (
          <div className="mt-6 p-4 bg-green-50 dark:bg-green-950 border border-green-200 dark:border-green-800 rounded-lg">
            <div className="flex items-center gap-2 mb-2">
              <CheckCircle className="h-5 w-5 text-green-600" />
              <h3 className="font-semibold text-green-800 dark:text-green-200">
                🎉 Onboarding Complete!
              </h3>
            </div>
            <p className="text-sm text-green-700 dark:text-green-300 mb-3">
              You're all set up! Your dashboard will now show real-time metrics as you run tests.
            </p>
            <Button
              size="sm"
              onClick={() => navigate('/runs')}
              className="bg-green-600 hover:bg-green-700"
            >
              View Your Runs
              <ExternalLink className="ml-1 h-3 w-3" />
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default OnboardingChecklist;